'use client';

import { useCallback, useEffect, useState } from 'react';

import { logger } from '@/lib/logger.client';
import { type IShippingLocation, shippingService } from '@/services';

type ShippingLocationSelectorProps = {
  providerId: string;
  providerName: string;
  zipcode?: string;
  /** addresstype filter sent to the V3 API (e.g. 'sucursal', 'domicilio'). Defaults to 'sucursal'. */
  addressType?: string;
  preloadedLocation?: IShippingLocation;
  onLocationSelected: (location: IShippingLocation) => void;
};

export const ShippingLocationSelector: React.FC<ShippingLocationSelectorProps> = ({ providerId, providerName, zipcode, addressType = 'sucursal', preloadedLocation, onLocationSelected }) => {
  const [locations, setLocations] = useState<IShippingLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<IShippingLocation | null>(preloadedLocation || null);
  const [zipcodeInput, setZipcodeInput] = useState(zipcode || preloadedLocation?.zipcode || '');

  const loadLocations = useCallback(
    async (zip?: string) => {
      setIsLoading(true);
      try {
        const params: Record<string, string> = {};
        if (zip) params.zipcode = zip;
        if (addressType) params.type = addressType;
        const response = await shippingService.getShippingLocations(providerId, params);

        const locationList = Array.isArray(response) ? response : [response];
        setLocations(locationList as IShippingLocation[]);

        logger.debug({ msg: 'Locations loaded', count: locationList.length });
      } catch (error) {
        logger.error({ msg: 'Error loading shipping locations', error });
        setLocations([]);
      } finally {
        setIsLoading(false);
      }
    },
    [providerId, addressType],
  );

  const handleSearchLocations = () => {
      loadLocations(zipcodeInput);
  };

  const handleLocationSelect = (location: IShippingLocation) => {
    setSelectedLocation(location);
    onLocationSelected(location);
    logger.info({ msg: 'Location selected and propagated', location });
  };

  // Create unique identifier for location comparison
  const getLocationId = (location: IShippingLocation) => {
    return location.id || `${location.zipcode}-${location.street_name}-${location.street_number}`;
  };

  useEffect(() => {
    const zip = zipcode || preloadedLocation?.zipcode;
    if (zip) {
      loadLocations(zip);
    }
    // preloadedLocation?.zipcode is stable enough; avoids re-running when only
    // the parent re-renders and creates a new object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipcode, preloadedLocation?.zipcode, providerId, loadLocations]);

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Seleccionar punto de {providerName === 'OCA' ? 'retiro OCA' : 'retiro'}</h3>

      {/* Zipcode Search */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-600 mb-2">Código Postal:</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={zipcodeInput}
            onChange={(e) => setZipcodeInput(e.target.value)}
            placeholder="Ingrese código postal"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          />
          <button
            onClick={handleSearchLocations}
            disabled={isLoading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Locations List */}
      {isLoading ? (
        <div className="text-center py-4 text-sm text-gray-500">Cargando ubicaciones...</div>
      ) : locations.length > 0 ? (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {locations.map((location) => {
            const locationId = getLocationId(location);
            const selectedId = selectedLocation ? getLocationId(selectedLocation) : null;
            const isSelected = locationId === selectedId;

            return (
              <label
                key={locationId}
                className={`
                                    flex items-start p-3 border rounded-lg cursor-pointer transition-all text-sm
                                    ${isSelected ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300 bg-white'}
                                `}
              >
                <input type="radio" name="location" checked={isSelected} onChange={() => handleLocationSelect(location)} className="mt-0.5 mr-3 text-primary focus:ring-primary" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{location.name || `${location.street_name || ''} ${location.street_number || ''}`.trim() || 'Dirección no disponible'}</p>
                  <p className="text-xs text-gray-600">
                    {location.location}
                    {location.province ? `, ${location.province}` : ''} - CP: {location.zipcode || 'N/A'}
                  </p>
                  {location.phone && <p className="text-xs text-gray-500 mt-1">📞 {location.phone}</p>}
                  {location.schedule_info && <p className="text-xs text-gray-500 mt-1">🕒 {location.schedule_info}</p>}
                </div>
              </label>
            );
          })}
        </div>
      ) : Boolean(zipcodeInput) && !isLoading ? (
        <div className="text-center py-4 text-sm text-gray-500">No se encontraron ubicaciones para este código postal.</div>
      ) : null}
    </div>
  );
};

export default ShippingLocationSelector;
